import { useMemo } from 'react';
import {
  NoShowRateByType,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsHooks, ChartCTA, InfoTipPopover } from '@frontend/analytics-shared';
import { AreaChartData } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { Text } from '@frontend/design-system';
import { nsrTimePeriods } from '../components/filters';
import { NSRTypeComparisonText } from '../components/nsr-type-comparison-text';
import { getTimelineValuePair, useFetchNSRROITimeline } from '../hooks/use-fetch-nsr-roi-timeline';
import { useNSRRoiShallowStore } from '../hooks/use-nsr-roi-store';
import { NSRRoiTimelineChartBase } from './nsr-roi-timeline-chart-base';
import { CommonChartProps } from './types';

export const NSRRoiPatientsTypeTimelineChart = (props: CommonChartProps) => {
  const { t } = useTranslation('analytics');
  const { filters } = useNSRRoiShallowStore('filters');

  const timePeriod =
    filters?.startDate && filters?.endDate
      ? AnalyticsHooks.findExistingTimePeriodKey<typeof nsrTimePeriods>(
          filters.startDate,
          filters.endDate,
          nsrTimePeriods
        ) ?? undefined
      : undefined;

  const { aggregates, isLoading, rawData } = useFetchNSRROITimeline({
    additionalRequestPayload: {
      roiType: RoiType.WEAVE,
      noShowRateType: NoShowRateByType.PATIENT_TYPE,
    },
    filters,
  });

  const { highlight, description } = useMemo(() => {
    const newPatients = Math.round(aggregates?.['New Appt'] || 0);
    const existingPatients = Math.round(aggregates?.['Follow Up Appt'] || 0);

    if (newPatients > existingPatients) {
      return {
        highlight: t('New Patients'),
        description: t('Send additional reminders for new patients to reduce no-shows'),
      };
    }

    if (existingPatients > newPatients) {
      return {
        highlight: t('Existing Patients'),
        description: t('Send additional reminders for existing patients to reduce no-shows'),
      };
    }

    if (existingPatients === 0 && existingPatients === newPatients) {
      return {
        highlight: t('NA'),
        description: t('No appointment data available to determine patient type no-show rates'),
      };
    }

    return {
      highlight: t('New & Existing Patients'),
      description: t('Send additional reminders for all patients to reduce no-shows'),
    };
  }, [aggregates, t]);

  const chartData = useMemo<AreaChartData>(() => {
    return {
      groups:
        rawData?.data?.dates?.map(({ key = '', params = [] }) => {
          const valuePair = getTimelineValuePair(params);

          return {
            name: key,
            values: {
              newPatients: valuePair['New Appt'] ?? 0,
              existingPatients: valuePair['Follow Up Appt'] ?? 0,
            },
          };
        }) || [],
    };
  }, [rawData]);

  return (
    <NSRRoiTimelineChartBase
      {...props}
      afterChart={
        <ChartCTA
          buttonLabel={t('Send Reminder')}
          ctaType='sendReminders'
          title={t('Highest No-Show')}
          highlight={highlight}
          description={description}
        />
      }
      chartData={chartData}
      infoTip={<InfoTipPopover>{t('Track no-shows based on the patient type')}</InfoTipPopover>}
      isLoading={isLoading}
      legendValues={{
        newPatients: formatters.appendPercent(aggregates?.['New Appt'] || 0),
        existingPatients: formatters.appendPercent(aggregates?.['Follow Up Appt'] || 0),
      }}
      tooltipBottomContentRenderer={(data) => (
        <Text color='subdued' size='small' style={{ maxWidth: '230px' }}>
          <NSRTypeComparisonText
            forTooltip
            formerMetric={t('New patients')}
            formerValue={data.newPatients}
            latterMetric={t('Existing patients')}
            latterValue={data.existingPatients}
            timePeriod={timePeriod}
          />
        </Text>
      )}
      showLegends
      title={t('No-Show Rate by Patient Type')}
      trackingIdBase='no-show-rate-patients-type'
    />
  );
};
